import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
});

// Attach token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Clear token on 401
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (firstName, lastName, email, password) =>
    API.post('/api/auth/register', { firstName, lastName, email, password }),
  login: (email, password) =>
    API.post('/api/auth/login', { email, password }),
  getMe: () => API.get('/api/auth/me'),
  forgotPassword: (email) =>
    API.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, password) =>
    API.post('/api/auth/reset-password', { token, password }),
};

// Trail Systems
export const getSystems = () => API.get('/api/trails/systems');
export const getSystem = (id) => API.get(`/api/trails/systems/${id}`);
export const createSystem = (data) => API.post('/api/trails/systems', data);
export const updateSystem = (id, data) => API.put(`/api/trails/systems/${id}`, data);
export const deleteSystem = (id) => API.delete(`/api/trails/systems/${id}`);

// Trails
export const addTrail = (systemId, data) => API.post(`/api/trails/systems/${systemId}/trails`, data);
export const updateTrail = (systemId, trailId, data) => API.put(`/api/trails/systems/${systemId}/trails/${trailId}`, data);
export const deleteTrail = (systemId, trailId) => API.delete(`/api/trails/systems/${systemId}/trails/${trailId}`);

// Stats
export const getStats = () => API.get('/api/trails/stats');

// Recommendations & Explore
export const getRecommendations = () => API.get('/api/trails/recommendations');
export const addRecommendedSystem = (id) => API.post(`/api/trails/systems/${id}/add`);
export const exploreSystems = () => API.get('/api/trails/explore');

// Strava
export const getStravaStatus = () => API.get('/api/strava/status');
export const getStravaAuthUrl = () => API.get('/api/strava/auth');
export function syncStravaSSE(onEvent) {
  const baseURL = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');
  const eventSource = new EventSource(`${baseURL}/api/strava/sync?token=${encodeURIComponent(token)}`);

  eventSource.addEventListener('status', (e) => onEvent('status', JSON.parse(e.data)));
  eventSource.addEventListener('progress', (e) => onEvent('progress', JSON.parse(e.data)));
  eventSource.addEventListener('complete', (e) => {
    onEvent('complete', JSON.parse(e.data));
    eventSource.close();
  });
  eventSource.addEventListener('error', (e) => {
    try { onEvent('error', JSON.parse(e.data)); } catch { onEvent('error', { error: 'Connection lost' }); }
    eventSource.close();
  });

  return eventSource;
}

// User location
export const updateLocation = (lat, lng) => API.put('/api/trails/location', { lat, lng });

// Friends
export const getFriends = () => API.get('/api/friends');
export const getFriendRequests = () => API.get('/api/friends/requests');
export const getPendingRequests = () => API.get('/api/friends/pending');
export const sendFriendRequest = (email) => API.post('/api/friends/request', { email });
export const acceptFriendRequest = (id) => API.put(`/api/friends/${id}/accept`);
export const rejectFriendRequest = (id) => API.put(`/api/friends/${id}/reject`);
export const removeFriend = (id) => API.delete(`/api/friends/${id}`);
export const getFriendProfile = (friendId) => API.get(`/api/friends/${friendId}/profile`);
export const getFriendComparison = (friendId, systemId) => API.get(`/api/friends/${friendId}/compare/${systemId}`);
export const updateUsername = (username) => API.put('/api/friends/username', { username });
export const checkUsername = (username) => API.get('/api/friends/username/check', { params: { username } });
export const getFriendsOnSystem = (systemId) => API.get(`/api/friends/on-system/${systemId}`);

// Groups
export const getGroups = () => API.get('/api/groups');
export const createGroup = (data) => API.post('/api/groups', data);
export const getGroupDetail = (id) => API.get(`/api/groups/${id}`);
export const joinGroup = (inviteCode) => API.post('/api/groups/join', { inviteCode });
export const leaveGroup = (id) => API.delete(`/api/groups/${id}/leave`);
export const deleteGroup = (id) => API.delete(`/api/groups/${id}`);

// Admin
export const getAdminUsers = () => API.get('/api/admin/users');
export const getAdminSystems = () => API.get('/api/admin/systems');
export const getAdminSystem = (id) => API.get(`/api/admin/systems/${id}`);
export const adminAddTrail = (systemId, data) => API.post(`/api/admin/systems/${systemId}/trails`, data);
export const adminUpdateTrail = (systemId, trailId, data) => API.put(`/api/admin/systems/${systemId}/trails/${trailId}`, data);
export const adminDeleteTrail = (systemId, trailId) => API.delete(`/api/admin/systems/${systemId}/trails/${trailId}`);
export const getAdminHotspots = (systemId) => API.get(`/api/admin/systems/${systemId}/hotspots`);
export const getAdminRides = () => API.get('/api/admin/rides');
export const getAdminRideCoords = (activityId) => API.get(`/api/admin/rides/${activityId}`);

// Trail Search (OpenStreetMap)
export const searchTrailAreas = (query) => API.get('/api/trailforks/search', { params: { query } });
export const getAreaTrails = (bbox) => API.get('/api/trailforks/region', { params: bbox });
export const importAreaTrails = (data) => API.post('/api/trailforks/import', data);
